import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import "./styles/Login.css";
import UserApiService from "../../../../services/apiServices/UserApiService";
import genericUtility from "../../../../services/utility/GenericUtility";
import { fetchProfile } from "../../../../services/thunks/ProfileThunk";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { useDispatch } from "react-redux";
import { data, useNavigate } from "react-router-dom";
import { closeAuth, openAuth } from "../../../../dataManagement/uiDb/GeneralDb";

interface LoginProps {
  setActiveTab: (activeTab: string) => {};
}

const Login: React.FC<LoginProps> = ({ setActiveTab }) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState<{ phonenumber: string; password: string }>({
    phonenumber: "",
    password: "",
  });

  const [errors, setErrors] = useState<{ phone: string; password: string }>({
    phone: "",
    password: "",
  });

  const validate = (): boolean => {
    let valid = true;
    const newErrors = { phone: "", password: "" };

    const phone = (form?.phonenumber ?? "").toString().trim();
    const password = (form?.password ?? "").toString();

    // Phone validation (basic India format or 10 digits)
    if (!phone) {
      newErrors.phone = "Phone number is required.";
      valid = false;
    } else if (!/^[0-9]{10}$/.test(phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
      valid = false;
    }

    // Password validation
    if (!password || !password.trim()) {
      newErrors.password = "Password is required.";
      valid = false;
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const encryptPassword = async (password: string) => {
    if (!password) return "";
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await window.crypto?.subtle?.digest("SHA-256", data);
    if (!hashBuffer) return "";
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // reset errors to empty object
    setErrors({ phone: "", password: "" });

    if (!validate()) return;

    try {
      const encryptedPassword = await encryptPassword(form.password ?? "");

      const payload = {
        phonenumber: form.phonenumber ?? "",
        password: form.password ?? "",
        encryptedPassword,
      };

      const response = await UserApiService.login(payload).catch((err) => {
        console.error("Login error:", err);
        return null;
      });

      if (!response) {
        setErrors((prev) => ({
          ...(prev ?? {}),
          password: "Check your internet connection",
        }));
        return;
      }

      if (response.message && response.message.includes("credentials")) {
        setErrors((prev) => ({
          ...(prev ?? {}),
          password: "Invalid credentials, please try again",
        }));
        return;
      } else if (response?.accessToken && response?.userId) {
        genericUtility.setTokenDetails(
          response.accessToken,
          response.userId,
          response.refreshToken
        );
      } else {
        setErrors((prev) => ({
          ...(prev ?? {}),
          password: "Technical issue, please report to support team",
        }));
        return;
      }

      await dispatch(fetchProfile())
        .unwrap()
        .catch((err) => {
          console.warn("fetchProfile failed:", err);
        });

      const returnUrl = genericUtility.getReturnUrl();
      genericUtility.clearReturnUrl();
      navigate(returnUrl || "/home", { replace: true });
      dispatch(closeAuth());
    } catch (err) {
      console.error(err);
      setErrors((prev) => ({
        ...(prev ?? {}),
        password: "An error occurred.",
      }));
    }
  };

  return (
    <form className="login-container" onSubmit={handleLogin}>
      {/* PHONE FIELD */}
      <div className="field-group">
        <label>Phone Number</label>
        <input
          type="text"
          name="phonenumber"
          autoFocus
          className={errors?.phone ? "input-error" : ""}
          value={form.phonenumber}
          onChange={handleInputChange}
          placeholder="Enter phone number"
        />
        {errors.phone && <p className="error-text">{errors.phone}</p>}
      </div>
      {/* PASSWORD FIELD */}
      <div className="field-group">
        <label>Password</label>
        <div className="password-wrapper">
          <input
            type={showPassword ? "text" : "password"}
            name="password"
            className={errors?.password ? "input-error" : ""}
            value={form.password}
            placeholder="Enter password"
            onChange={handleInputChange}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        {errors.password && <p className="error-text">{errors.password}</p>}
      </div>

      <button
        type="button"
        className="forgot-password"
        onClick={() => setActiveTab("forgotpassword")}
      >
        Forgot Password?
      </button>
      <button className="primary-btn" type="submit">
        Login
      </button>
    </form>
  );
};

export default Login;
